import { useState, useEffect, useRef, useCallback } from "react";
import dicomParser from "dicom-parser";
import JSZip from "jszip";
import { useDicomStore } from "../../redux/dicomRedux";
import { gunzipSync } from "fflate";
import { anatomyConstants } from "../../constants/anatomyConstants";

/* ====================== constants & helpers ====================== */
const AXIS_COLORS = {
  x: "rgba(255, 92, 92, 0.95)", // sagittal / X
  y: "rgba(92, 255, 120, 0.95)", // coronal  / Y
  z: "rgba(92, 148, 255, 0.95)", // axial    / Z
};

const CROSSHAIR_THICKNESS = 2;
const MASK_ALPHA = 0.6; // fixed 60% opacity for both mask and shell overlays

const buildLUT = (center, width) => {
  const MIN_HU = -4096,
    MAX_HU = 4096;
  const size = MAX_HU - MIN_HU + 1;
  const lut = new Uint8ClampedArray(size);
  const lo = center - width / 2;
  const hi = center + width / 2;
  for (let hu = MIN_HU; hu <= MAX_HU; hu++) {
    let g;
    if (hu <= lo) g = 0;
    else if (hu >= hi) g = 255;
    else g = Math.round(((hu - lo) / width) * 255);
    lut[hu - MIN_HU] = g;
  }
  return { lut, MIN_HU };
};

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

const drawCrosshair = (canvas, x, y, vColor, hColor, thicknessMul = 1) => {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.save();
  const base = Math.max(
    1,
    Math.round(Math.min(canvas.width, canvas.height) / 512)
  );
  ctx.lineWidth = Math.max(1, Math.round(base * thicknessMul));

  // vertical
  ctx.strokeStyle = vColor;
  ctx.beginPath();
  ctx.moveTo(x + 0.5, 0);
  ctx.lineTo(x + 0.5, canvas.height);
  ctx.stroke();

  // horizontal
  ctx.strokeStyle = hColor;
  ctx.beginPath();
  ctx.moveTo(0, y + 0.5);
  ctx.lineTo(canvas.width, y + 0.5);
  ctx.stroke();

  ctx.restore();
};

/* ====================== component ====================== */
const DicomViewer = ({ dicomFile }) => {
  // --- Zustand selectors (guard each with a fallback) ---
  const volume = useDicomStore((s) => s.volume);
  const crosshair = useDicomStore((s) => s.crosshairIJK);
  const pointLinkingEnabled =
    useDicomStore((s) => s.pointLinkingEnabled) ?? false;

  const setVolume = useDicomStore((s) => s.setVolume) || ((_) => {});
  const clearVolume = useDicomStore((s) => s.clearVolume) || (() => {});
  const setDicomBounds = useDicomStore((s) => s.setDicomBounds) || ((_) => {});
  const setDicomOrientation =
    useDicomStore((s) => s.setDicomOrientation) || ((_) => {});
  const setCrosshairIJK =
    useDicomStore((s) => s.setCrosshairIJK) || ((_) => {});

  const suppressPublishRef = useRef(false);

  // indices
  const [zIndex, setZIndex] = useState(0);
  const [yIndex, setYIndex] = useState(0);
  const [xIndex, setXIndex] = useState(0);

  // WL per view
  const [zCenter, setZCenter] = useState(1024);
  const [zWidth, setZWidth] = useState(2000);
  const [yCenter, setYCenter] = useState(1024);
  const [yWidth, setYWidth] = useState(2000);
  const [xCenter, setXCenter] = useState(1024);
  const [xWidth, setXWidth] = useState(2000);

  // crosshair toggle
  const [trackingEnabled, setTrackingEnabled] = useState(true);

  // ===== Overlay (mask or shell) =====
  // Lazy-load design: only fetch when overlay is ON and that source is selected.
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [overlayMode, setOverlayMode] = useState("mask"); // "mask" | "shell"
  const [overlayRGBAVol, setOverlayRGBAVol] = useState(null); // currently active RGBA vol

  // caches for both sources, persist per dicomFile load
  const overlayCacheRef = useRef({ mask: null, shell: null });
  const overlayLoadingRef = useRef({ mask: false, shell: false });

  // Reusable buffers for re-sliced (coronal/sagittal) RGBA
  const maskYSliceRGBARef = useRef(null); // Uint8ClampedArray(cols*depth*4)
  const maskXSliceRGBARef = useRef(null); // Uint8ClampedArray(rows*depth*4)

  // Offscreen canvases for proper alpha blending (drawImage does blending; putImageData doesn't)
  const offZRef = useRef(null);
  const offYRef = useRef(null);
  const offXRef = useRef(null);

  // canvases
  const rootRef = useRef(null);
  const canvasZRef = useRef(null);
  const canvasYRef = useRef(null);
  const canvasXRef = useRef(null);

  // resliced buffers & imageData
  const ySliceRef = useRef(null);
  const xSliceRef = useRef(null);
  const imgDataZRef = useRef(null);
  const imgDataYRef = useRef(null);
  const imgDataXRef = useRef(null);

  // rAF throttling
  const rafIdRef = useRef(null);
  const drawPendingRef = useRef(false);

  // loading
  const loadTokenRef = useRef(0);
  const workerRef = useRef(null);

  // drag state
  const draggingRef = useRef(null); // "axial" | "coronal" | "sagittal" | null

  /* ===== external crosshair → local (only if linking) ===== */
  useEffect(() => {
    if (!crosshair || !pointLinkingEnabled || !volume) return;
    const { rows, cols, depth } = volume;
    suppressPublishRef.current = true;
    setXIndex(clamp(Math.round(crosshair.i), 0, cols - 1));
    setYIndex(clamp(Math.round(crosshair.j), 0, rows - 1));
    setZIndex(clamp(Math.round(crosshair.k), 0, depth - 1));
    const t = setTimeout(() => (suppressPublishRef.current = false), 0);
    return () => clearTimeout(t);
  }, [crosshair, pointLinkingEnabled, volume]);

  /* ===== local → external crosshair ===== */
  const publishCrosshair = (i, j, k) => {
    if (!pointLinkingEnabled) return;
    if (suppressPublishRef.current) return;
    setCrosshairIJK({ i, j, k });
  };

  /* ===== worker setup (best effort) ===== */
  useEffect(() => {
    try {
      workerRef.current = new Worker(
        new URL("./resampleWorker.js", import.meta.url),
        { type: "module" }
      );
    } catch (e) {
      console.warn("Worker creation failed; will use non-resampled volume.", e);
      workerRef.current = null;
    }
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  function extractSpacing(ds) {
    const ps = ds.string("x00280030")?.split("\\").map(Number);
    let ss = Number(ds.string("x00180088"));
    if (!ss || isNaN(ss)) ss = Number(ds.string("x00180050"));
    if (!ps || ps.length < 2) throw new Error("Missing PixelSpacing");
    if (!ss || isNaN(ss)) throw new Error("Missing slice spacing");
    return [ps[1], ps[0], ss]; // [spacingX(cols), spacingY(rows), spacingZ(depth)]
  }

  /* ===================== Overlay JSON: fetch + gunzip + RGBA volume ===================== */

  async function fetchOverlayJSON(url) {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Overlay HTTP ${resp.status}`);
    const gz = new Uint8Array(await resp.arrayBuffer());
    const raw = gunzipSync(gz);
    const txt = new TextDecoder("utf-8").decode(raw);
    return JSON.parse(txt); // { grid:{depth,rows,cols}, order:"z,y,x", palette, spans }
  }

  function decodeLabelsToDense(grid, spans) {
    const D = grid.depth | 0,
      H = grid.rows | 0,
      W = grid.cols | 0;
    const HW = H * W;
    const flat = new Uint16Array(D * HW); // 0 = background
    for (let i = 0; i < spans.length; i++) {
      const start = spans[i][0] | 0,
        len = spans[i][1] | 0,
        sid = spans[i][2] | 0;
      if (len <= 0) continue;
      const lo = Math.max(0, start);
      const hi = Math.min(flat.length, start + len);
      if (hi > lo) flat.fill(sid, lo, hi);
    }
    const data = new Array(D);
    for (let z = 0; z < D; z++) data[z] = flat.subarray(z * HW, (z + 1) * HW);
    return { data, rows: H, cols: W, depth: D };
  }

  function labelsToRGBAVolume(grid, labelSlices, palette) {
    const D = grid.depth | 0,
      H = grid.rows | 0,
      W = grid.cols | 0;
    const HW = H * W;
    const rgbaSlices = new Array(D);
    for (let z = 0; z < D; z++) {
      const labels = labelSlices[z];
      const out = new Uint8ClampedArray(HW * 4);
      let p = 0;
      for (let i = 0; i < HW; i++, p += 4) {
        const sid = labels[i] | 0;
        if (sid === 0) {
          out[p + 3] = 0;
        } else {
          const rgba = palette[sid - 1];
          if (rgba) {
            out[p] = rgba[0] | 0;
            out[p + 1] = rgba[1] | 0;
            out[p + 2] = rgba[2] | 0;
            const a = (rgba[3] ?? 255) | 0;
            out[p + 3] = Math.max(0, Math.min(255, Math.round(a * MASK_ALPHA)));
          } else {
            out[p + 3] = 0;
          }
        }
      }
      rgbaSlices[z] = out;
    }
    return { data: rgbaSlices, rows: H, cols: W, depth: D };
  }

  async function loadJSONRGBAVolume(url) {
    const payload = await fetchOverlayJSON(url);
    const { grid, order, palette = [], spans = [] } = payload || {};
    if (!grid || order !== "z,y,x")
      throw new Error("Unexpected overlay payload/order");
    const labelVol = decodeLabelsToDense(grid, spans);
    return labelsToRGBAVolume(grid, labelVol.data, palette);
  }

  // When dicomFile changes, reset overlay caches and choose default source
  useEffect(() => {
    overlayCacheRef.current = { mask: null, shell: null };
    overlayLoadingRef.current = { mask: false, shell: false };
    setOverlayRGBAVol(null);

    // default mode: prefer mask if present, else shell
    const defaultMode = dicomFile?.maskUrl
      ? "mask"
      : dicomFile?.shellUrl
      ? "shell"
      : "mask";
    setOverlayMode(defaultMode);

    // auto-enable overlay only if at least one URL exists
    const hasAny = !!dicomFile?.maskUrl || !!dicomFile?.shellUrl;
    setOverlayEnabled(hasAny ? true : false);
  }, [dicomFile?.url, dicomFile?.maskUrl, dicomFile?.shellUrl]);

  // Ensure the selected overlay is loaded when enabled or when mode changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!overlayEnabled || !volume) {
        setOverlayRGBAVol(null);
        return;
      }

      const src = overlayMode; // "mask" | "shell"
      const url = src === "mask" ? dicomFile?.maskUrl : dicomFile?.shellUrl;

      if (!url) {
        console.warn(`Overlay source "${src}" has no URL; overlay disabled.`);
        setOverlayRGBAVol(null);
        return;
      }

      // if cached, use it
      if (overlayCacheRef.current[src]) {
        setOverlayRGBAVol(overlayCacheRef.current[src]);
        return;
      }

      // avoid duplicate fetches
      if (overlayLoadingRef.current[src]) return;
      overlayLoadingRef.current[src] = true;

      try {
        const rgbaVol = await loadJSONRGBAVolume(url);
        if (cancelled) return;

        // verify dims match current DICOM volume
        if (
          !volume ||
          rgbaVol.rows !== volume.rows ||
          rgbaVol.cols !== volume.cols ||
          rgbaVol.depth !== volume.depth
        ) {
          console.warn(`Overlay(${src}) dims != DICOM dims; overlay ignored.`, {
            overlay: {
              rows: rgbaVol.rows,
              cols: rgbaVol.cols,
              depth: rgbaVol.depth,
            },
            vol: volume && {
              rows: volume.rows,
              cols: volume.cols,
              depth: volume.depth,
            },
          });
          overlayCacheRef.current[src] = null;
          if (overlayMode === src) setOverlayRGBAVol(null);
          return;
        }

        overlayCacheRef.current[src] = rgbaVol;
        if (overlayMode === src) setOverlayRGBAVol(rgbaVol);
      } catch (e) {
        console.error(`Failed to load overlay (${src}):`, e);
        overlayCacheRef.current[src] = null;
        if (overlayMode === src) setOverlayRGBAVol(null);
      } finally {
        overlayLoadingRef.current[src] = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    overlayEnabled,
    overlayMode,
    dicomFile?.maskUrl,
    dicomFile?.shellUrl,
    volume,
  ]);

  // Helpers to re-slice RGBA overlay
  function buildCoronalRGBA(rgbaVol, yIdx, cols, depth) {
    // output: (w,h) = (cols, depth), bottom-up z to match gray coronal
    const HW = cols * depth;
    let out = maskYSliceRGBARef.current;
    if (!out || out.length !== HW * 4) {
      out = new Uint8ClampedArray(HW * 4);
      maskYSliceRGBARef.current = out;
    }
    for (let z = 0; z < depth; z++) {
      const sliceRGBA = rgbaVol.data[z]; // rows*cols*4
      const src = sliceRGBA.subarray(
        yIdx * cols * 4,
        yIdx * cols * 4 + cols * 4
      );
      const dstBase = (depth - 1 - z) * cols * 4;
      out.set(src, dstBase);
    }
    return out;
  }

  function buildSagittalRGBA(rgbaVol, xIdx, rows, depth, cols) {
    // output: (w,h) = (rows, depth), bottom-up z to match gray sagittal
    const HW = rows * depth;
    let out = maskXSliceRGBARef.current;
    if (!out || out.length !== HW * 4) {
      out = new Uint8ClampedArray(HW * 4);
      maskXSliceRGBARef.current = out;
    }
    for (let z = 0; z < depth; z++) {
      const sliceRGBA = rgbaVol.data[z]; // rows*cols*4
      const dstRowBase = (depth - 1 - z) * rows * 4;
      for (let y = 0; y < rows; y++) {
        const srcPix = (y * cols + xIdx) * 4;
        const dstPix = dstRowBase + y * 4;
        out[dstPix] = sliceRGBA[srcPix];
        out[dstPix + 1] = sliceRGBA[srcPix + 1];
        out[dstPix + 2] = sliceRGBA[srcPix + 2];
        out[dstPix + 3] = sliceRGBA[srcPix + 3];
      }
    }
    return out;
  }

  function drawOverlayRGBA(ctx, overlayRGBA, w, h, offRef) {
    if (!overlayRGBA) return;
    let off = offRef.current;
    if (!off) {
      off = document.createElement("canvas");
      offRef.current = off;
    }
    if (off.width !== w || off.height !== h) {
      off.width = w;
      off.height = h;
    }
    const octx = off.getContext("2d");
    const img = new ImageData(overlayRGBA, w, h);
    octx.putImageData(img, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(off, 0, 0);
  }

  /* ===== DICOM loading with bounds/orientation ===== */
  useEffect(() => {
    if (!dicomFile?.url) {
      clearVolume();
      return;
    }

    let cancelled = false;
    const myToken = ++loadTokenRef.current;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(dicomFile.url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled || myToken !== loadTokenRef.current) return;

        const zip = await JSZip.loadAsync(buf);
        const files = [];
        zip.forEach((_, f) => files.push(f));

        const parsed = await Promise.all(
          files.map(async (file) => {
            try {
              const ab = await file.async("arraybuffer");
              const bytes = new Uint8Array(ab);
              const ds = dicomParser.parseDicom(bytes);
              if (!ds.elements?.x7fe00010) return null;

              const rows = ds.uint16("x00280010");
              const cols = ds.uint16("x00280011");
              const elem = ds.elements.x7fe00010;
              const pixelRep = ds.uint16("x00280103") || 0; // 0=unsigned,1=signed
              const view = new DataView(
                ds.byteArray.buffer,
                elem.dataOffset,
                rows * cols * 2
              );
              const slope = parseFloat(ds.floatString("x00281053") || 1);
              const intercept = parseFloat(ds.floatString("x00281052") || 0);

              const hu = new Int16Array(rows * cols);
              for (let i = 0; i < hu.length; i++) {
                const raw =
                  pixelRep === 0
                    ? view.getUint16(i * 2, true)
                    : view.getInt16(i * 2, true);
                hu[i] = raw * slope + intercept;
              }

              const pos = ds.string("x00200032").split("\\").map(Number);
              return { hu, rows, cols, z: pos[2], position: pos, ds };
            } catch {
              return null;
            }
          })
        );

        const slices = parsed.filter(Boolean);
        if (!slices.length) throw new Error("No valid DICOM slices");
        slices.sort((a, b) => a.z - b.z);

        const dsFirst = slices[0].ds;
        const orientationArr =
          dsFirst.string("x00200037")?.split("\\").map(Number) ?? [];
        const pixelSpacing =
          dsFirst.string("x00280030")?.split("\\").map(Number) ?? [];
        const rowsNum = dsFirst.uint16("x00280010");
        const colsNum = dsFirst.uint16("x00280011");
        const rowVec = orientationArr.slice(0, 3);
        const colVec = orientationArr.slice(3, 6);

        // normal = row × col
        const cross = (a, b) => [
          a[1] * b[2] - a[2] * b[1],
          a[2] * b[0] - a[0] * b[2],
          a[0] * b[1] - a[1] * b[0],
        ];
        const normal = cross(rowVec, colVec);
        const normLen = Math.hypot(normal[0], normal[1], normal[2]) || 1;
        const normalUnit = normal.map((v) => v / normLen);

        const firstPos = slices[0].position;
        const lastPos = slices[slices.length - 1].position;
        const diff = lastPos.map((v, i) => v - firstPos[i]);
        const stackLen =
          diff[0] * normalUnit[0] +
          diff[1] * normalUnit[1] +
          diff[2] * normalUnit[2];
        const sliceSpacing =
          slices.length > 1 ? stackLen / (slices.length - 1) : 1;

        function getWorld(row, col, sliceIdx) {
          return [
            firstPos[0] +
              row * pixelSpacing[0] * colVec[0] +
              col * pixelSpacing[1] * rowVec[0] +
              sliceIdx * sliceSpacing * normalUnit[0],
            firstPos[1] +
              row * pixelSpacing[0] * colVec[1] +
              col * pixelSpacing[1] * rowVec[1] +
              sliceIdx * sliceSpacing * normalUnit[1],
            firstPos[2] +
              row * pixelSpacing[0] * colVec[2] +
              col * pixelSpacing[1] * rowVec[2] +
              sliceIdx * sliceSpacing * normalUnit[2],
          ];
        }

        const corners = [
          getWorld(0, 0, 0),
          getWorld(0, colsNum - 1, 0),
          getWorld(rowsNum - 1, 0, 0),
          getWorld(rowsNum - 1, colsNum - 1, 0),
          getWorld(0, 0, slices.length - 1),
          getWorld(0, colsNum - 1, slices.length - 1),
          getWorld(rowsNum - 1, 0, slices.length - 1),
          getWorld(rowsNum - 1, colsNum - 1, slices.length - 1),
        ];

        // guarded no-ops if not provided in store
        setDicomBounds({ corners });
        setDicomOrientation({ row: rowVec, col: colVec });

        // base (non-resampled) volume for immediate render
        const rows = slices[0].rows;
        const cols = slices[0].cols;
        const depth = slices.length;
        const SLICE_SIZE = rows * cols;
        const base = { data: new Array(depth), rows, cols, depth };
        for (let z = 0; z < depth; z++) base.data[z] = slices[z].hu;

        // try isotropic resample; fall back to base on failure
        const tryResample = !!workerRef.current;
        if (tryResample) {
          const [spacingX, spacingY, spacingZ] = extractSpacing(dsFirst);
          const targetSpacing = Math.min(spacingX, spacingY, spacingZ);

          // flatten for worker
          const flat = new Int16Array(SLICE_SIZE * depth);
          for (let z = 0; z < depth; z++)
            flat.set(base.data[z], z * SLICE_SIZE);

          const isotropic = await new Promise((resolve) => {
            let settled = false;
            const onMessage = (evt) => {
              if (settled) return;
              settled = true;
              workerRef.current?.removeEventListener("message", onMessage);
              const { dst, rows: nRows, cols: nCols, depth: nDepth } = evt.data;
              const OUT_SLICE = nRows * nCols;
              const arr = new Array(nDepth);
              for (let z = 0; z < nDepth; z++) {
                arr[z] = dst.subarray(z * OUT_SLICE, (z + 1) * OUT_SLICE);
              }
              resolve({ data: arr, rows: nRows, cols: nCols, depth: nDepth });
            };
            const onError = (err) => {
              if (settled) return;
              settled = true;
              console.warn("Worker error; using base volume.", err);
              workerRef.current?.removeEventListener("message", onMessage);
              resolve(null);
            };
            workerRef.current?.addEventListener("message", onMessage);
            workerRef.current?.addEventListener("error", onError, {
              once: true,
            });
            workerRef.current?.postMessage(
              {
                src: flat,
                rows,
                cols,
                depth,
                spacingX,
                spacingY,
                spacingZ,
                targetSpacing,
              },
              [flat.buffer]
            );
            // safety timeout
            setTimeout(() => {
              if (!settled) {
                console.warn("Worker timeout; using base volume.");
                workerRef.current?.removeEventListener("message", onMessage);
                resolve(null);
              }
            }, 8000);
          });

          if (isotropic && !cancelled && myToken === loadTokenRef.current) {
            setVolume(isotropic);
            setZIndex(Math.floor(isotropic.depth / 2));
            setYIndex(Math.floor(isotropic.rows / 2));
            setXIndex(Math.floor(isotropic.cols / 2));
            return;
          }
        }

        // fallback to base
        if (!cancelled && myToken === loadTokenRef.current) {
          setVolume(base);
          setZIndex(Math.floor(base.depth / 2));
          setYIndex(Math.floor(base.rows / 2));
          setXIndex(Math.floor(base.cols / 2));
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Failed to load DICOM zip:", err);
        clearVolume();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      drawPendingRef.current = false;
    };
  }, [dicomFile, setVolume, clearVolume, setDicomBounds, setDicomOrientation]);

  /* ===== drawing ===== */
  const drawPlane = useCallback(
    (canvas, imgDataRef, grayArray, w, h, center, width) => {
      if (!canvas) return;
      const container = canvas.parentElement;
      if (!container) return;

      const containerW = Math.max(1, container.clientWidth);
      const containerH = Math.max(1, container.clientHeight);
      const containerAspect = containerW / containerH;
      const imageAspect = w / h;
      let displayW, displayH;
      if (containerAspect > imageAspect) {
        displayH = containerH;
        displayW = Math.max(1, Math.floor(displayH * imageAspect));
      } else {
        displayW = containerW;
        displayH = Math.max(1, Math.floor(displayW / imageAspect));
      }

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        imgDataRef.current = ctx.createImageData(w, h);
      }
      canvas.style.width = `${displayW}px`;
      canvas.style.height = `${displayH}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx || !imgDataRef.current) return;
      const { lut, MIN_HU } = buildLUT(center, width);
      const img = imgDataRef.current;
      const data = img.data;

      for (let i = 0; i < grayArray.length; i++) {
        const hu = grayArray[i];
        const g = lut[Math.max(0, Math.min(lut.length - 1, hu - MIN_HU))];
        const p = i * 4;
        data[p] = g;
        data[p + 1] = g;
        data[p + 2] = g;
        data[p + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
    },
    []
  );

  const scheduleDraw = useCallback(() => {
    if (drawPendingRef.current) return;
    drawPendingRef.current = true;
    rafIdRef.current = requestAnimationFrame(() => {
      drawPendingRef.current = false;
      if (!volume) return;

      const { data, rows, cols, depth } = volume;

      // Axial (Z)
      {
        const canvas = canvasZRef.current;
        drawPlane(
          canvas,
          imgDataZRef,
          data[zIndex],
          cols,
          rows,
          zCenter,
          zWidth
        );
        if (overlayEnabled && overlayRGBAVol) {
          const ctx = canvas.getContext("2d");
          const rgba = overlayRGBAVol.data[zIndex];
          drawOverlayRGBA(ctx, rgba, cols, rows, offZRef);
        }
        if (trackingEnabled) {
          drawCrosshair(
            canvas,
            clamp(xIndex, 0, cols - 1),
            clamp(yIndex, 0, rows - 1),
            AXIS_COLORS.x,
            AXIS_COLORS.y,
            CROSSHAIR_THICKNESS
          );
        }
      }

      // Coronal (Y): (w,h) = (cols, depth)
      {
        if (!ySliceRef.current || ySliceRef.current.length !== cols * depth) {
          ySliceRef.current = new Int16Array(cols * depth);
        }
        const yBuf = ySliceRef.current;
        for (let z = 0; z < depth; z++) {
          const sl = data[z];
          for (let x = 0; x < cols; x++) {
            yBuf[(depth - 1 - z) * cols + x] = sl[yIndex * cols + x];
          }
        }
        const canvas = canvasYRef.current;
        drawPlane(canvas, imgDataYRef, yBuf, cols, depth, yCenter, yWidth);
        if (overlayEnabled && overlayRGBAVol) {
          const ctx = canvas.getContext("2d");
          const rgba = buildCoronalRGBA(overlayRGBAVol, yIndex, cols, depth);
          drawOverlayRGBA(ctx, rgba, cols, depth, offYRef);
        }
        if (trackingEnabled) {
          drawCrosshair(
            canvas,
            clamp(xIndex, 0, cols - 1),
            clamp(depth - 1 - zIndex, 0, depth - 1),
            AXIS_COLORS.x,
            AXIS_COLORS.z,
            CROSSHAIR_THICKNESS
          );
        }
      }

      // Sagittal (X): (w,h) = (rows, depth)
      {
        if (!xSliceRef.current || xSliceRef.current.length !== rows * depth) {
          xSliceRef.current = new Int16Array(rows * depth);
        }
        const xBuf = xSliceRef.current;
        for (let y = 0; y < rows; y++) {
          for (let z = 0; z < depth; z++) {
            xBuf[y + (depth - 1 - z) * rows] = data[z][y * cols + xIndex];
          }
        }
        const canvas = canvasXRef.current;
        drawPlane(canvas, imgDataXRef, xBuf, rows, depth, xCenter, xWidth);
        if (overlayEnabled && overlayRGBAVol) {
          const ctx = canvas.getContext("2d");
          const rgba = buildSagittalRGBA(
            overlayRGBAVol,
            xIndex,
            rows,
            depth,
            cols
          );
          drawOverlayRGBA(ctx, rgba, rows, depth, offXRef);
        }
        if (trackingEnabled) {
          drawCrosshair(
            canvas,
            clamp(yIndex, 0, rows - 1),
            clamp(depth - 1 - zIndex, 0, depth - 1),
            AXIS_COLORS.y,
            AXIS_COLORS.z,
            CROSSHAIR_THICKNESS
          );
        }
      }
    });
  }, [
    volume,
    zIndex,
    yIndex,
    xIndex,
    zCenter,
    zWidth,
    yCenter,
    yWidth,
    xCenter,
    xWidth,
    trackingEnabled,
    overlayEnabled,
    overlayRGBAVol,
    drawPlane,
  ]);

  // draw when volume arrives
  useEffect(() => {
    if (!volume) return;
    scheduleDraw();
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      drawPendingRef.current = false;
    };
  }, [volume, scheduleDraw]);

  // draw when indices / WL / toggles / overlay change
  useEffect(() => {
    if (!volume) return;
    scheduleDraw();
  }, [
    zIndex,
    yIndex,
    xIndex,
    zCenter,
    zWidth,
    yCenter,
    yWidth,
    xCenter,
    xWidth,
    trackingEnabled,
    overlayEnabled,
    overlayRGBAVol,
    volume,
    scheduleDraw,
  ]);

  // redraw on container resize
  useEffect(() => {
    if (!rootRef.current) return;
    const ro = new ResizeObserver(() => scheduleDraw());
    ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, [scheduleDraw]);

  /* ===== pointer handlers ===== */
  const eventToCanvasPix = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    const xCss = clientX - rect.left;
    const yCss = clientY - rect.top;
    const x = Math.round((xCss / rect.width) * canvas.width);
    const y = Math.round((yCss / rect.height) * canvas.height);
    return { x, y };
  };

  const onAxialPointer = useCallback(
    (e) => {
      if (!volume || !trackingEnabled) return;
      const c = canvasZRef.current;
      if (!c) return;
      const { rows, cols } = volume;
      const { x, y } = eventToCanvasPix(c, e.clientX, e.clientY);
      const xi = clamp(x, 0, cols - 1);
      const yi = clamp(y, 0, rows - 1);
      setXIndex(xi);
      setYIndex(yi);
      publishCrosshair(xi, yi, zIndex);
    },
    [volume, trackingEnabled, zIndex]
  );

  const onCoronalPointer = useCallback(
    (e) => {
      if (!volume || !trackingEnabled) return;
      const c = canvasYRef.current;
      if (!c) return;
      const { cols, depth } = volume;
      const { x, y } = eventToCanvasPix(c, e.clientX, e.clientY);
      const xi = clamp(x, 0, cols - 1);
      const zi = clamp(depth - 1 - y, 0, depth - 1);
      setXIndex(xi);
      setZIndex(zi);
      publishCrosshair(xi, yIndex, zi);
    },
    [volume, trackingEnabled, yIndex]
  );

  const onSagittalPointer = useCallback(
    (e) => {
      if (!volume || !trackingEnabled) return;
      const c = canvasXRef.current;
      if (!c) return;
      const { rows, depth } = volume;
      const { x, y } = eventToCanvasPix(c, e.clientX, e.clientY);
      const yi = clamp(x, 0, rows - 1);
      const zi = clamp(depth - 1 - y, 0, depth - 1);
      setYIndex(yi);
      setZIndex(zi);
      publishCrosshair(xIndex, yi, zi);
    },
    [volume, trackingEnabled, xIndex]
  );

  const handlePointerDown = (which) => (e) => {
    draggingRef.current = which;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (which === "axial") onAxialPointer(e);
    if (which === "coronal") onCoronalPointer(e);
    if (which === "sagittal") onSagittalPointer(e);
  };
  const handlePointerMove = (e) => {
    if (!e.buttons || e.buttons === 0) return; // not dragging
    const which = draggingRef.current;
    if (which === "axial") onAxialPointer(e);
    if (which === "coronal") onCoronalPointer(e);
    if (which === "sagittal") onSagittalPointer(e);
  };
  const handlePointerUp = (e) => {
    draggingRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  /* ===== UI ===== */
  const horizontalSliderStyles = {
    container:
      "w-full flex items-center h-8 bg-[#1e1e1e] border-b border-gray-700",
    sliderContainer: "flex items-center flex-grow px-2",
    slider:
      "w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer",
    label: "text-xs text-[var(--main-color)] ml-2 min-w-[60px]",
  };
  const verticalSliderStyles = {
    container:
      "flex flex-col items-center justify-center h-full w-8 bg-[#1e1e1e] border-l border-gray-700 portrait:hidden",
    sliderContainer: "flex-grow flex items-center",
    slider:
      "h-full w-2.5 [-webkit-appearance:slider-vertical] bg-gray-700 rounded-lg appearance-none cursor-pointer",
    label: "text-xs text-[var(--main-color)] mt-2 rotate-90",
  };
  const mobileHorizontalSliderStyles = {
    container:
      "hidden portrait:flex items-center h-8 bg-[#1e1e1e] border-b border-gray-700",
    sliderContainer: "flex items-center flex-grow px-2",
    slider:
      "w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer",
    label: "text-xs text-[var(--main-color)] ml-2 min-w-[60px]",
  };

  const hasMask = !!dicomFile?.maskUrl;
  const hasShell = !!dicomFile?.shellUrl;

  return (
    <div
      ref={rootRef}
      className="relative w-full h-full"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      {/* Global triangulation toggle */}
      <button
        className="fixed top-24 portrait:top-40 left-2 z-50 bg-[#1e1e1e]/80 text-[var(--main-color)] border border-gray-600 rounded px-2 py-1 hover:bg-[#2a2a2a]"
        onClick={() => setTrackingEnabled((v) => !v)}
        title="Toggle triangulation (crosshair sync)"
      >
        {trackingEnabled ? "Triangulation: ON" : "Triangulation: OFF"}
      </button>

      {/* Overlay ON/OFF (under triangulation) - only show if mask or shell exists */}
      {(hasMask || hasShell) && (
        <button
          className="fixed top-36 portrait:top-52 left-2 z-50 bg-[#1e1e1e]/80 text-[var(--main-color)] border border-gray-600 rounded px-2 py-1 hover:bg-[#2a2a2a]"
          onClick={() => setOverlayEnabled((v) => !v)}
          title="Toggle overlay (mask/shell)"
        >
          {overlayEnabled ? "Overlay: ON" : "Overlay: OFF"}
        </button>
      )}

      {/* Overlay source toggle (Mask <-> Shell) - only show if both exist */}
      {hasMask && hasShell && (
        <button
          className="fixed top-48 portrait:top-64 left-2 z-50 bg-[#1e1e1e]/80 text-[var(--main-color)] border border-gray-600 rounded px-2 py-1 hover:bg-[#2a2a2a]"
          onClick={() =>
            setOverlayMode((m) => (m === "mask" ? "shell" : "mask"))
          }
          title="Switch overlay source (Mask/Shell)"
        >
          {overlayMode === "mask" ? "Source: Mask" : "Source: Shell"}
        </button>
      )}

      {/* Axial */}
      <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-black flex flex-col">
        <div className={horizontalSliderStyles.container}>
          <div className={horizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={horizontalSliderStyles.slider}
              min={0}
              max={volume?.depth - 1 || 0}
              step={1}
              value={zIndex}
              onChange={(e) => {
                const k = +e.target.value;
                setZIndex(k);
                publishCrosshair(xIndex, yIndex, k);
              }}
            />
            <span className={horizontalSliderStyles.label}>
              {zIndex + 1}/{volume?.depth || 0}
            </span>
          </div>
        </div>
        {/* Mobile horizontal sliders for window level/width (portrait only) */}
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={-1024}
              max={3072}
              step={1}
              value={zCenter}
              onChange={(e) => setZCenter(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>
              {zCenter}
            </span>
          </div>
        </div>
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={1}
              max={4096}
              step={1}
              value={zWidth}
              onChange={(e) => setZWidth(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>{zWidth}</span>
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="flex-grow flex items-center justify-center">
            <canvas
              ref={canvasZRef}
              className="max-w-full max-h-full object-contain"
              style={{ cursor: "crosshair" }}
              onPointerDown={handlePointerDown("axial")}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          </div>
          <div className="flex flex-row h-full">
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={-1024}
                  max={3072}
                  step={1}
                  value={zCenter}
                  onChange={(e) => setZCenter(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{zCenter}</span>
            </div>
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={1}
                  max={4096}
                  step={1}
                  value={zWidth}
                  onChange={(e) => setZWidth(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{zWidth}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Coronal */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-black flex flex-col">
        <div className={horizontalSliderStyles.container}>
          <div className={horizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={horizontalSliderStyles.slider}
              min={0}
              max={volume?.rows - 1 || 0}
              step={1}
              value={yIndex}
              onChange={(e) => {
                const j = +e.target.value;
                setYIndex(j);
                publishCrosshair(xIndex, j, zIndex);
              }}
            />
            <span className={horizontalSliderStyles.label}>
              {yIndex + 1}/{volume?.rows || 0}
            </span>
          </div>
        </div>
        {/* Mobile horizontal sliders for window level/width (portrait only) */}
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={-1024}
              max={3072}
              step={1}
              value={yCenter}
              onChange={(e) => setYCenter(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>
              {yCenter}
            </span>
          </div>
        </div>
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={1}
              max={4096}
              step={1}
              value={yWidth}
              onChange={(e) => setYWidth(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>{yWidth}</span>
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="flex-grow flex items-center justify-center">
            <canvas
              ref={canvasYRef}
              className="max-w-full max-h-full object-contain"
              style={{ cursor: "crosshair" }}
              onPointerDown={handlePointerDown("coronal")}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          </div>
          <div className="flex flex-row h-full">
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={-1024}
                  max={3072}
                  step={1}
                  value={yCenter}
                  onChange={(e) => setYCenter(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{yCenter}</span>
            </div>
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={1}
                  max={4096}
                  step={1}
                  value={yWidth}
                  onChange={(e) => setYWidth(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{yWidth}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sagittal */}
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-black flex flex-col">
        <div className={horizontalSliderStyles.container}>
          <div className={horizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={horizontalSliderStyles.slider}
              min={0}
              max={volume?.cols - 1 || 0}
              step={1}
              value={xIndex}
              onChange={(e) => {
                const i = +e.target.value;
                setXIndex(i);
                publishCrosshair(i, yIndex, zIndex);
              }}
            />
            <span className={horizontalSliderStyles.label}>
              {xIndex + 1}/{volume?.cols || 0}
            </span>
          </div>
        </div>
        {/* Mobile horizontal sliders for window level/width (portrait only) */}
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={-1024}
              max={3072}
              step={1}
              value={xCenter}
              onChange={(e) => setXCenter(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>
              {xCenter}
            </span>
          </div>
        </div>
        <div className={mobileHorizontalSliderStyles.container}>
          <div className={mobileHorizontalSliderStyles.sliderContainer}>
            <input
              type="range"
              className={mobileHorizontalSliderStyles.slider}
              min={1}
              max={4096}
              step={1}
              value={xWidth}
              onChange={(e) => setXWidth(+e.target.value)}
              style={{ accentColor: "var(--main-color)" }}
            />
            <span className={mobileHorizontalSliderStyles.label}>{xWidth}</span>
          </div>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="flex-grow flex items-center justify-center">
            <canvas
              ref={canvasXRef}
              className="max-w-full max-h-full object-contain"
              style={{ cursor: "crosshair" }}
              onPointerDown={handlePointerDown("sagittal")}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          </div>
          <div className="flex flex-row h-full">
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={-1024}
                  max={3072}
                  step={1}
                  value={xCenter}
                  onChange={(e) => setXCenter(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{xCenter}</span>
            </div>
            <div className={verticalSliderStyles.container + " px-2 py-2"}>
              <div className={verticalSliderStyles.sliderContainer}>
                <input
                  type="range"
                  className={verticalSliderStyles.slider}
                  min={1}
                  max={4096}
                  step={1}
                  value={xWidth}
                  onChange={(e) => setXWidth(+e.target.value)}
                  orient="vertical"
                  style={{ accentColor: "var(--main-color)" }}
                />
              </div>
              <span className={verticalSliderStyles.label}>{xWidth}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DicomViewer;
